// Structural / statistical summaries of a parsed genome.
//
// Deliberately non-diagnostic: these are quality-control and composition
// metrics about the data itself (call rate, zygosity, Ti/Tv, per-chromosome
// density). i-seng does not map variants to traits, conditions, or risk.

import { CHROM_ORDER, type ParsedGenome, type Variant } from '../types';

const BASES = new Set(['A', 'C', 'G', 'T']);
const TRANSITIONS = new Set(['AG', 'GA', 'CT', 'TC']);

export interface ChromCount {
  chrom: string;
  count: number;
}

export interface GenomeStats {
  total: number;
  called: number;
  noCalls: number;
  callRate: number; // 0..1
  homozygous: number;
  heterozygous: number;
  hetRate: number; // heterozygous / called SNPs (0..1)
  transitions: number;
  transversions: number;
  tiTv: number | null;
  perChrom: ChromCount[];
  genotypeCounts: { label: string; count: number }[];
}

function isNoCall(g: string): boolean {
  return g === '' || g === '--' || g === '..' || g === '00' || /[-.]/.test(g);
}

/** Extract the two single-base alleles from an array genotype, else null. */
function twoBaseAlleles(g: string): [string, string] | null {
  if (g.length !== 2) return null;
  const a = g[0].toUpperCase();
  const b = g[1].toUpperCase();
  if (!BASES.has(a) || !BASES.has(b)) return null;
  return [a, b];
}

export function computeStats(genome: ParsedGenome): GenomeStats {
  const variants = genome.variants;
  const total = variants.length;
  const perChromMap = new Map<string, number>();
  const genotypeMap = new Map<string, number>();

  let noCalls = 0;
  let homozygous = 0;
  let heterozygous = 0;
  let transitions = 0;
  let transversions = 0;

  for (let i = 0; i < variants.length; i++) {
    const v = variants[i];
    perChromMap.set(v.chrom, (perChromMap.get(v.chrom) ?? 0) + 1);

    const g = v.genotype;
    genotypeMap.set(g, (genotypeMap.get(g) ?? 0) + 1);

    if (isNoCall(g)) {
      noCalls++;
      continue;
    }

    const alleles = twoBaseAlleles(g);
    if (alleles) {
      const [a, b] = alleles;
      if (a === b) {
        homozygous++;
      } else {
        heterozygous++;
        // Classify the base change for heterozygous SNPs.
        if (TRANSITIONS.has(a + b)) transitions++;
        else transversions++;
      }
    } else if (v.ref && v.alt) {
      // VCF-style ref>alt change (single-base SNVs only).
      const ref = v.ref.toUpperCase();
      const alt = v.alt.split(',')[0].toUpperCase();
      if (BASES.has(ref) && BASES.has(alt)) {
        if (ref === alt) homozygous++;
        else {
          if (TRANSITIONS.has(ref + alt)) transitions++;
          else transversions++;
        }
      }
    }
  }

  const called = total - noCalls;
  const perChrom: ChromCount[] = CHROM_ORDER
    .map((chrom) => ({ chrom, count: perChromMap.get(chrom) ?? 0 }))
    .filter((c) => c.count > 0);

  // Include any unrecognized contigs at the end so nothing is silently dropped.
  for (const [chrom, count] of perChromMap) {
    if (!CHROM_ORDER.includes(chrom)) perChrom.push({ chrom, count });
  }

  const genotypeCounts = [...genotypeMap.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);

  return {
    total,
    called,
    noCalls,
    callRate: total > 0 ? called / total : 0,
    homozygous,
    heterozygous,
    hetRate: called > 0 ? heterozygous / called : 0,
    transitions,
    transversions,
    tiTv: transversions > 0 ? transitions / transversions : null,
    perChrom,
    genotypeCounts,
  };
}

/** Case-insensitive filter over rsID / chromosome / position / genotype. */
export function filterVariants(
  variants: Variant[],
  query: string,
  chrom: string | 'all',
): Variant[] {
  const q = query.trim().toLowerCase();
  const out: Variant[] = [];
  for (let i = 0; i < variants.length; i++) {
    const v = variants[i];
    if (chrom !== 'all' && v.chrom !== chrom) continue;
    if (q) {
      const hay =
        v.rsid.toLowerCase() +
        ' ' + v.chrom.toLowerCase() +
        ' ' + String(v.pos) +
        ' ' + v.genotype.toLowerCase();
      if (!hay.includes(q)) continue;
    }
    out.push(v);
  }
  return out;
}
